import { ProductionUnit } from "@tosize/ui";
import { match } from "ts-pattern";
import { z } from "zod";

export const defaultImportSchemaItem = {
	quantity: null,
	length: null,
	width: null,
	label: "",
	finishesEnabled: false,
} satisfies ListImportFormType["items"][number];

const sides = ["length1", "length2", "width1", "width2"] as const;

export type SidesType = (typeof sides)[number];

const finishes = [
	"afronden_up",
	"afronden_down",
	"afronden_both",
	"afschuinen_up",
	"afschuinen_down",
	"afschuinen_both",
	"matching",
	"custom",
] as const;

export type FinishesType = (typeof finishes)[number];

const numberSchema = (min = 1) => {
	return z
		.number()
		.or(z.null())
		.superRefine((val, ctx) => {
			if (val === null) {
				ctx.addIssue({
					code: z.ZodIssueCode.invalid_type,
					expected: "number",
					received: "null",
					fatal: true,
				});

				return z.NEVER;
			}

			if (val < min) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					params: { type: "too_small_unit", unit: "mm", minimum: min },
				});
			}
		});
};

const itemSchema = z.object({
	quantity: numberSchema(),
	length: numberSchema(50),
	width: numberSchema(50),
	label: z.string().optional(),
	finishesEnabled: z.boolean(),
	finishes: z
		.object({
			sides: z.array(z.enum(sides)).min(1),
			type: z.enum(finishes),
			radius: z.number().optional(),
			depth: z.number().optional(),
			thickness: z.number().optional(),
			sku: z.string().optional(),
		})
		.optional(),
});

export type ListImportFormType = z.infer<typeof listImportFormSchema>;

export const listImportFormSchema = z
	.object({
		grouped_product_id: z.string().min(1),
		simple_product_id: z.string().min(1),
		production_type: z
			.nativeEnum(ProductionUnit)
			.or(z.null())
			.refine((val) => val !== null),
		items: z.array(itemSchema).min(1),
	})
	.superRefine((data, ctx) => {
		for (const [index, item] of data.items.entries()) {
			if (item.finishesEnabled && item.finishes !== undefined) {
				match(item.finishes)
					.with(
						{ type: "afronden_up" },
						{ type: "afronden_down" },
						{ type: "afronden_both" },
						(t) => {
							if (t.radius === undefined) {
								ctx.addIssue({
									code: z.ZodIssueCode.invalid_type,
									expected: "number",
									received: "undefined",
									path: ["items", index, "finishes", "radius"],
								});
							}
						},
					)
					.with(
						{ type: "afschuinen_up" },
						{ type: "afschuinen_down" },
						{ type: "afschuinen_both" },
						(t) => {
							if (t.depth === undefined) {
								ctx.addIssue({
									code: z.ZodIssueCode.invalid_type,
									expected: "number",
									received: "undefined",
									path: ["items", index, "finishes", "depth"],
								});
							}
						},
					)
					.with({ type: "matching" }, (t) => {
						if (t.sides.length !== sides.length) {
							ctx.addIssue({
								code: z.ZodIssueCode.too_small,
								type: "array",
								minimum: sides.length,
								path: ["items", index, "finishes", "sides"],
								inclusive: true,
							});
						}

						if (t.thickness === undefined) {
							ctx.addIssue({
								code: z.ZodIssueCode.invalid_type,
								expected: "number",
								received: "undefined",
								path: ["items", index, "finishes", "thickness"],
							});
						}
					})
					.with({ type: "custom" }, (t) => {
						if (t.sides.length !== sides.length) {
							ctx.addIssue({
								code: z.ZodIssueCode.too_small,
								type: "array",
								minimum: sides.length,
								path: ["items", index, "finishes", "sides"],
								inclusive: true,
							});
						}

						if (t.thickness === undefined) {
							ctx.addIssue({
								code: z.ZodIssueCode.invalid_type,
								expected: "number",
								received: "undefined",
								path: ["items", index, "finishes", "thickness"],
							});
						}

						if (t.sku === undefined) {
							ctx.addIssue({
								code: z.ZodIssueCode.invalid_type,
								expected: "string",
								received: "undefined",
								path: ["items", index, "finishes", "sku"],
							});
						}
					})
					.run();
			}
		}
	});
